import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'
import { useSnackbar } from '@app/context/SnackbarContext'

import { Modal } from '@ui/components/Modal'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { copyToClipboard } from '@utils/navigator.utils'

import {
  IntroducingBrokerReferralDetailsDto,
  IntroducingBrokerReferralTypes,
} from '@model/IntroducingBrokerDetailsDto'
import { NameDto } from '@model/NameDto'
import { ReferralMaterialPreviewDto } from '@model/ReferralMaterialDto'

import { Locale } from '@global/locale/Locale'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'

import { PromoImagePreview } from './PromoImagePreview'
import { PromoPreviewHtmlCode } from './PromoPreviewHtmlCode'
import { PromoPreviewUrlFields } from './PromoPreviewUrlFields'
import { PromoReferralCodeField } from './PromoReferralCodeField'
import { PromoSizeLanguageField } from './PromoSizeLanguageField'

import styles from './PromoPreviewModal.module.scss'

interface Props {
  mtCode?: string
  referralCodes: IntroducingBrokerReferralDetailsDto[]
  referralMaterialId: string
  query?: PageQuery
  language?: Locale
}

export interface PromoAssetFields {
  language: NameDto<string> | null
  size: NameDto | null
  referralCode: {
    id: IntroducingBrokerReferralTypes
    name: string
  } | null
  defaultTargetUrl: NameDto<string> | null
  customTargetUrl: string | null
  urlOutput: string | null
  htmlCode: string | null
}

const initPromoAssetValues = {
  language: null,
  size: null,
  referralCode: null,
  defaultTargetUrl: null,
  customTargetUrl: null,
  urlOutput: null,
  htmlCode: null,
}

export const CUSTOM_URL_PROMO_OPTION_ID = '0'

export const PromoPreviewModal: FC<Props> = ({
  mtCode,
  referralCodes,
  referralMaterialId,
  query,
  language,
}) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const isMobile = useWindowResize()
  const caLang = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const domain = window.location.host
  const [referralMaterial, setReferralMaterial] = useState<ReferralMaterialPreviewDto | null>(null)
  const locale = useSessionLanguage()

  const [fields, setField] = useState<PromoAssetFields>(initPromoAssetValues)
  const [isLinkLoading, setIsLinkLoading] = useState(false)
  const [isLinkShortened, setIsLinkShortened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const isDefaultTargetUrlSelected =
    fields.defaultTargetUrl && fields.defaultTargetUrl.id !== CUSTOM_URL_PROMO_OPTION_ID

  const selectedPromoAsset = useMemo(() => {
    return referralMaterial?.promoAssets.find((promo) => {
      return promo.isDefault
    })
  }, [referralMaterial])

  const fetchReferralMaterial = useCallback(
    async (referralMaterialId: string, language: Locale = locale, query?: PageQuery) => {
      setIsLoading(true)

      try {
        const resp = await apiClient.getReferralMaterial(
          referralMaterialId,
          language,
          domain,
          query
        )

        setReferralMaterial(resp)
        return resp
      } finally {
        setIsLoading(false)
      }
    },
    [apiClient, domain, locale]
  )

  useEffect(() => {
    fetchReferralMaterial(referralMaterialId, language, query)
  }, [fetchReferralMaterial, language, query, referralMaterialId])

  const handleIBLinkGeneration = async (shouldShortenUrl: boolean) => {
    if ((shouldShortenUrl && isLinkShortened) || isLinkLoading) {
      return
    }

    setIsLinkLoading(true)

    if (isLinkShortened && !shouldShortenUrl) {
      setIsLinkShortened(false)
    }

    try {
      if (
        referralMaterial?.id &&
        fields.referralCode?.name &&
        (fields.defaultTargetUrl?.id || fields.customTargetUrl)
      ) {
        const response = await apiClient.generateReferralUrl({
          domain,
          referralMaterialId: referralMaterial?.id,
          defaultUrlId: isDefaultTargetUrlSelected ? fields.defaultTargetUrl?.id : undefined,
          customUrl: fields.customTargetUrl ?? undefined,
          ibCode: fields.referralCode.name,
          shortenUrl: shouldShortenUrl,
        })
        const htmlCode = generateHTMLCode(response.shortenUrl || response.url)

        setField((prevFields) => ({
          ...prevFields,
          urlOutput: response.shortenUrl || response.url,
          htmlCode,
        }))

        if (shouldShortenUrl && !response.shortenUrl) {
          addSnackbar.error({ message: t('Referral Materials.Shorten link was not generated') })
          return
        } else if (response.shortenUrl || response.url) {
          addSnackbar.success({ message: t('Validation.Link was successfully generated') })
        }

        shouldShortenUrl && setIsLinkShortened(true)
      }
      return
    } finally {
      setIsLinkLoading(false)
    }
  }

  const generateHTMLCode = (url: string | null) => {
    const [width, height] = fields.size?.name.split('x') || []
    const src = selectedPromoAsset?.imagePath ?? 'IMAGE PATH'

    return `<a href="${
      url ?? `[${t('Referral Materials.TARGET URL')}]`
    }" target="_blank"><img alt="Tickmill promo banner" src="${src}" width="${
      width ?? 0
    }" height="${height ?? 0}"/></a>`
  }

  useEffect(() => {
    if (referralMaterial) {
      const mainIbCode = referralCodes.find(
        (referralCode) => referralCode.mainIbScheme.id === IntroducingBrokerReferralTypes.Default
      )

      const referralIbCode = mainIbCode ?? referralCodes[0]
      const preSelectedPreviewPromo = referralMaterial.promoAssets.find((promo) => promo.isDefault)
      const isDefaultUrlsListEmpty = referralMaterial.urls.length === 0

      setField((prevFields) => ({
        ...prevFields,
        language: prevFields.language ?? preSelectedPreviewPromo?.language ?? null,
        referralCode: referralIbCode
          ? { id: referralIbCode?.mainIbScheme.id, name: referralIbCode.code }
          : null,
        size: preSelectedPreviewPromo?.size ?? null,
        defaultTargetUrl: isDefaultUrlsListEmpty
          ? { id: CUSTOM_URL_PROMO_OPTION_ID, name: t('Custom') }
          : null,
      }))
    }
  }, [referralCodes, referralMaterial, t])

  useEffect(() => {
    const hasTargetUrlSelected =
      isDefaultTargetUrlSelected ||
      (fields.defaultTargetUrl?.id === CUSTOM_URL_PROMO_OPTION_ID && fields.customTargetUrl)

    if (hasTargetUrlSelected) {
      handleIBLinkGeneration(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.defaultTargetUrl, fields.customTargetUrl, fields.referralCode])

  useEffect(() => {
    const htmlCode = generateHTMLCode(fields.urlOutput)
    setField((prevFields) => ({ ...prevFields, htmlCode }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromoAsset])

  const handleOnCodeCopyClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()
    if (!fields.htmlCode) {
      return
    }

    copyToClipboard(fields.htmlCode)
    addSnackbar.success({ message: t('Code copied') })
  }

  return (
    <Modal.FullScreen size='xl'>
      {isMobile && <Modal.Header>{t('Referral Materials.Promo view')}</Modal.Header>}
      <Modal.Grid.Row>
        <Modal.Grid.Column noPadding>
          {!isMobile && (
            <PromoImagePreview
              isRefMaterialLoading={isLoading}
              selectedPromoAsset={selectedPromoAsset}
            />
          )}
        </Modal.Grid.Column>
        <Modal.Grid.Column>
          {!isMobile && <Modal.Header canClose>{t('Referral Materials.Promo view')}</Modal.Header>}
          <Modal.Body>
            <FieldWrapper className={styles.fieldsContainer}>
              {isMobile && (
                <PromoImagePreview
                  isRefMaterialLoading={isLoading}
                  selectedPromoAsset={selectedPromoAsset}
                />
              )}
              {referralMaterial && (
                <PromoSizeLanguageField
                  isRefMaterialLoading={isLoading}
                  referralMaterial={referralMaterial}
                  caLang={caLang}
                  fields={fields}
                  setField={setField}
                  fetchReferralMaterial={fetchReferralMaterial}
                />
              )}

              {mtCode && (
                <Box fullWidth>
                  <PromoReferralCodeField
                    referralCodes={referralCodes}
                    fields={fields}
                    setField={setField}
                    isRefMaterialLoading={isLoading}
                  />
                </Box>
              )}

              {referralMaterial && (
                <PromoPreviewUrlFields
                  referralMaterial={referralMaterial}
                  fields={fields}
                  setField={setField}
                  isRefMaterialLoading={isLoading}
                  isLinkLoading={isLinkLoading}
                  isDefaultTargetUrlSelected={isDefaultTargetUrlSelected}
                  onIBLinkGeneration={handleIBLinkGeneration}
                  isLinkShortened={isLinkShortened}
                />
              )}

              <PromoPreviewHtmlCode fields={fields} />
            </FieldWrapper>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Secondary
              disabled={!fields.htmlCode || isLoading}
              onClick={handleOnCodeCopyClick}
            >
              {t('Copy code')}
            </Modal.Footer.Secondary>
            <Modal.Footer.Primary
              link={{ href: selectedPromoAsset?.imagePath, download: selectedPromoAsset?.name }}
            >
              {t('Download')}
            </Modal.Footer.Primary>
          </Modal.Footer>
        </Modal.Grid.Column>
      </Modal.Grid.Row>
    </Modal.FullScreen>
  )
}
